'use client'

import { useState, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { signInFormSchema, signUpFormSchema, verificationCodeSchema } from '@/lib/validations/auth'
import { useClerkAuth } from './useClerkAuth'
import { AuthResult } from '@/types/api'

type AuthMode = 'signin' | 'signup'

/**
 * Custom hook for managing authentication form state and logic
 */
export const useAuthForm = () => {
  const {
    signInUser,
    verifySignInCode,
    resendSignInCode,
    signUpUser,
    verifySignUpCode,
    resendSignUpCode,
    handleGoogleAuth,
  } = useClerkAuth()

  // Form state - consistent approach
  const [authMode, setAuthMode] = useState<AuthMode>('signin')
  const [isEmailSent, setIsEmailSent] = useState(false)
  const [userEmail, setUserEmail] = useState('')
  const [password, setPassword] = useState<string | undefined>('')

  // Form instances
  const signInForm = useForm({
    resolver: zodResolver(signInFormSchema),
    defaultValues: { email: '' },
  })

  const signUpForm = useForm({
    resolver: zodResolver(signUpFormSchema),
    defaultValues: { email: '' },
  })

  const codeForm = useForm({
    resolver: zodResolver(verificationCodeSchema),
    defaultValues: { code: '' },
  })

  // Centralized reset function
  const resetAllForms = useCallback(() => {
    setIsEmailSent(false)
    setUserEmail('')
    setPassword('')
    signInForm.reset()
    signUpForm.reset()
    codeForm.reset()
  }, [signInForm, signUpForm, codeForm])

  // Form submission handlers
  const handleSignInSubmit = useCallback(
    async (data: { email: string }): Promise<AuthResult | null> => {
      const result = await signInUser(data.email)

      if (result?.success) {
        setUserEmail(data.email)
        setIsEmailSent(true)
        return result
      }

      if (result?.shouldSwitchToSignUp) {
        // Account not found - automatically start sign-up flow instead of showing form
        signUpForm.setValue('email', data.email)
        setAuthMode('signup')
        return await handleSignUpSubmit({ email: data.email })
      }

      return result
    },
    [signInUser, signUpUser]
  )

  const handleSignUpSubmit = useCallback(
    async (data: { email: string; password?: string }): Promise<AuthResult | null> => {
      const result = await signUpUser(data)
      if (result?.success) {
        setUserEmail(data.email)
        setPassword(data.password)
        setIsEmailSent(true)
      }
      return result
    },
    [signUpUser]
  )

  const handleCodeSubmit = useCallback(
    async (data: { code: string }): Promise<AuthResult | null> => {
      const result = authMode === 'signin' ? await verifySignInCode(data.code) : await verifySignUpCode(data.code)
      return result
    },
    [authMode, verifySignInCode, verifySignUpCode]
  )

  const handleResend = useCallback(async (): Promise<AuthResult> => {
    const result =
      authMode === 'signin' ? await resendSignInCode(userEmail) : await resendSignUpCode({ email: userEmail, password })
    return result
  }, [authMode, userEmail, password, resendSignInCode, resendSignUpCode])

  const handleGoogleAuthWrapper = useCallback(async (): Promise<AuthResult> => {
    const result = await handleGoogleAuth()
    return result
  }, [handleGoogleAuth])

  // Mode switching
  const switchAuthMode = useCallback(() => {
    setAuthMode((prev) => (prev === 'signin' ? 'signup' : 'signin'))
    resetAllForms()
  }, [resetAllForms])

  // Navigation handlers
  const handleGoBack = useCallback(() => {
    resetAllForms()
  }, [resetAllForms])

  return {
    // State
    authMode,
    isEmailSent,
    userEmail,

    // Forms
    signInForm,
    signUpForm,
    codeForm,

    // Handlers
    handleSignInSubmit,
    handleSignUpSubmit,
    handleCodeSubmit,
    handleResend,
    handleGoogleAuthWrapper,
    switchAuthMode,
    handleGoBack,
    resetAllForms,
  }
}
